import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useStore } from './hooks/useStore';
import ErrorBoundary from './components/ErrorBoundary';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/Layout/DashboardLayout';
import { can, firstAllowedRoute, ROUTE_PERMISSIONS } from './utils/permissions';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';

// Lazy load other pages for better performance
const UserRegistration = React.lazy(() => import('./pages/UserRegistration'));
const SubscriptionManagement = React.lazy(() => import('./pages/SubscriptionManagement'));
const SubscriptionStats = React.lazy(() => import('./pages/SubscriptionStats'));
const PlatformConfig = React.lazy(() => import('./pages/PlatformConfig'));
const LanguageManagement = React.lazy(() => import('./pages/LanguageManagement'));
const CommissionManagement = React.lazy(() => import('./pages/CommissionManagement'));
const CoinManagement = React.lazy(() => import('./pages/CoinManagement'));
const StripeManagement = React.lazy(() => import('./pages/StripeManagement'));
const NewsletterCampaigns = React.lazy(() => import('./pages/NewsletterCampaigns'));
const PackageManagement = React.lazy(() => import('./pages/PackageManagement'));
const NewsletterComposer = React.lazy(() => import('./pages/NewsletterComposer'));
const BlogArticles = React.lazy(() => import('./pages/BlogArticles'));
const BlogArticleEditor = React.lazy(() => import('./pages/BlogArticleEditor'));
const FicOAuthCallback = React.lazy(() => import('./pages/FicOAuthCallback'));
const LegalDocuments = React.lazy(() => import('./pages/LegalDocuments'));

const App: React.FC = () => {
  const { initializeAuth, isInitializing } = useStore();

  useEffect(() => {
    // Initialize auth only once when app starts
    initializeAuth().catch(console.warn);
  }, []); // Empty dependency array to run only once

  // Listen for 401 unauthorized events from the API interceptor
  useEffect(() => {
    const handleUnauthorized = () => {
      // Force logout without calling API (token is already invalid)
      useStore.setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        stats: null,
        languages: [],
        platformConfig: null
      });
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  // Show loading screen during initialization
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          {/* Toast notifications */}
          <Toaster
            position="top-right"
            richColors
            closeButton
            duration={4000}
          />

        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />

          {/* Protected routes - layout wrapper without path prefix */}
          <Route element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            {/* Dashboard home */}
            <Route path="/dashboard" element={<RequirePerm route="/dashboard"><Dashboard /></RequirePerm>} />

            {/* User management */}
            <Route path="/users" element={
              <RequirePerm route="/users">
              <React.Suspense fallback={<PageLoader />}>
                <UserRegistration />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Subscription management */}
            <Route path="/subscriptions" element={
              <RequirePerm route="/subscriptions">
              <React.Suspense fallback={<PageLoader />}>
                <SubscriptionManagement />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Subscription stats */}
            <Route path="/subscription-stats" element={
              <RequirePerm route="/subscription-stats">
              <React.Suspense fallback={<PageLoader />}>
                <SubscriptionStats />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Platform configuration */}
            <Route path="/config" element={
              <RequirePerm route="/config">
              <React.Suspense fallback={<PageLoader />}>
                <PlatformConfig />
              </React.Suspense>
              </RequirePerm>
            } />
            {/* Ritorno OAuth Fatture in Cloud (redirect URI dell'app) */}
            <Route path="/config/fic/callback" element={
              <RequirePerm route="/config">
              <React.Suspense fallback={<PageLoader />}>
                <FicOAuthCallback />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Documenti legali (privacy, cookie, termini) */}
            <Route path="/legal" element={
              <RequirePerm route="/legal">
              <React.Suspense fallback={<PageLoader />}>
                <LegalDocuments />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Language management */}
            <Route path="/languages" element={
              <RequirePerm route="/languages">
              <React.Suspense fallback={<PageLoader />}>
                <LanguageManagement />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Commission management */}
            <Route path="/commissions" element={
              <RequirePerm route="/commissions">
              <React.Suspense fallback={<PageLoader />}>
                <CommissionManagement />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Coin management */}
            <Route path="/coins" element={
              <RequirePerm route="/coins">
              <React.Suspense fallback={<PageLoader />}>
                <CoinManagement />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Stripe management */}
            <Route path="/stripe" element={
              <RequirePerm route="/stripe">
              <React.Suspense fallback={<PageLoader />}>
                <StripeManagement />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Catalogo pacchetti boost (categorie + listino) */}
            <Route path="/packages" element={
              <RequirePerm route="/packages">
              <React.Suspense fallback={<PageLoader />}>
                <PackageManagement />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Newsletter — list + composer.
                Order matters: /newsletter/new prima di /newsletter/:id (più specifico),
                ma React Router 6+ matcha sempre la route più specifica nell'ordine
                dichiarato. Qui sono comunque entrambi sotto /newsletter/, ok. */}
            <Route path="/newsletter" element={
              <RequirePerm route="/newsletter">
              <React.Suspense fallback={<PageLoader />}>
                <NewsletterCampaigns />
              </React.Suspense>
              </RequirePerm>
            } />
            <Route path="/newsletter/new" element={
              <RequirePerm route="/newsletter/new">
              <React.Suspense fallback={<PageLoader />}>
                <NewsletterComposer />
              </React.Suspense>
              </RequirePerm>
            } />
            <Route path="/newsletter/:id" element={
              <RequirePerm route="/newsletter/new">
              <React.Suspense fallback={<PageLoader />}>
                <NewsletterComposer />
              </React.Suspense>
              </RequirePerm>
            } />

            {/* Blog — lista + editor articoli (canale separato dalla newsletter) */}
            <Route path="/blog" element={
              <RequirePerm route="/blog">
              <React.Suspense fallback={<PageLoader />}>
                <BlogArticles />
              </React.Suspense>
              </RequirePerm>
            } />
            <Route path="/blog/new" element={
              <RequirePerm route="/blog">
              <React.Suspense fallback={<PageLoader />}>
                <BlogArticleEditor />
              </React.Suspense>
              </RequirePerm>
            } />
            <Route path="/blog/:id" element={
              <RequirePerm route="/blog">
              <React.Suspense fallback={<PageLoader />}>
                <BlogArticleEditor />
              </React.Suspense>
              </RequirePerm>
            } />
          </Route>

          {/* Redirect root to dashboard */}
          <Route path="/" element={<HomeRedirect />} />

          {/* 404 page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </div>
      </Router>
    </ErrorBoundary>
  );
};

/**
 * Gate RBAC di una route: senza il permesso si atterra sulla prima pagina
 * consentita (redirect, non errore: l'URL può essere un vecchio preferito).
 * Il gating FE è solo UX — l'enforcement vero sta in SecurityConfig sul BE.
 */
const RequirePerm: React.FC<{ route: string; children: React.ReactNode }> = ({ route, children }) => {
  const user = useStore((s) => s.user);
  if (!can(user, ...(ROUTE_PERMISSIONS[route] ?? []))) {
    const fallback = firstAllowedRoute(user);
    // Nessuna route permessa (permessi vuoti o non caricati): schermata
    // dedicata, MAI un redirect — punterebbe a una route a sua volta vietata
    // e girerebbe in loop.
    if (!fallback) return <NoAccess />;
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
};

/** "/" → prima pagina consentita (dipende dai permessi dell'utente). */
const HomeRedirect: React.FC = () => {
  const user = useStore((s) => s.user);
  const fallback = firstAllowedRoute(user);
  // Non autenticato: verso il login via route protetta di default.
  if (!user) return <Navigate to="/dashboard" replace />;
  if (!fallback) return <NoAccess />;
  return <Navigate to={fallback} replace />;
};

/** Admin autenticato ma senza alcuna sezione visibile. */
const NoAccess: React.FC = () => {
  const logout = useStore((s) => s.logout);
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
        <h2 className="text-lg font-bold text-slate-900 mb-2">Nessuna sezione disponibile</h2>
        <p className="text-sm text-slate-500 mb-6">
          La tua utenza non ha permessi attivi sulla dashboard. Chiedi a un amministratore di
          assegnarteli, poi effettua di nuovo l'accesso.
        </p>
        <button
          onClick={() => logout()}
          className="inline-flex items-center h-10 px-4 rounded-lg bg-indigo-600 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
        >
          Esci
        </button>
      </div>
    </div>
  );
};

// Loading component for lazy-loaded pages
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// 404 Not Found component
const NotFound: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center max-w-md mx-auto p-6">
      <div className="bg-gray-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
        <svg className="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h2>
      <p className="text-gray-600 mb-4">
        The page you're looking for doesn't exist or has been moved.
      </p>
      <a
        href="/dashboard"
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
      >
        Return to Dashboard
      </a>
    </div>
  </div>
);

export default App;
